/**
 * Clearing an organisation's data from a workbench or a node, and refusing machines while an Enterprise AIOE
 * is unlicensed (AIOE 0.59.0, JP 21 Oct 2026). AIOE's own contract; the workbench mirrors it into
 * `shared/schemas/deviceClear.ts` and the mesh agent reads the two refusal bodies.
 *
 * Clearing. An administrator of the organisation (Enterprise AIOE or a Mojo Up AI Cloud team) asks a machine to
 * delete what it holds of the organisation: `POST /admin/v1/fleet/devices/:id/clear { scope, remove, reason? }`.
 * The machine learns it whichever way it next speaks to the platform:
 *  - on a heartbeat answer, `clear`;
 *  - removed (`remove: true`, or revoked before), every call with its old access or refresh token is answered
 *    401 `{ error: "device_removed", clear? }`;
 *  - while the AIOE is unlicensed, 402 `{ error: "licence_required", clear? }`.
 * It deletes, then reports once with `POST /fleet/v1/clear/:id/ack` (its token, even a revoked one), and the
 * console shows the outcome. A clear is never undone and is repeated on every contact until acknowledged.
 *
 *  - `organisation`: everything the machine holds for the organisation's space: its sign-in tokens and sealing
 *    keys, the organisation's teams, policy, catalogue, skills and design systems, cached projects, boards,
 *    memory, inbox, organisation secrets and backups' keys, the mesh state and the node's held work. The
 *    person's own spaces, their Free projects and their repositories' working copies stay.
 *  - `organisation-and-repositories`: the above, and the working copies of the organisation's projects'
 *    repositories on the machine (after it has said which, in the acknowledgement).
 *
 * Unlicensed. An Enterprise AIOE whose trial has ended (`trial-ended`) or whose licence is past its grace
 * (`lapsed`) answers every machine 402 `licence_required` with its words, refuses enrolment and token refresh
 * the same way, and keeps the console open so an administrator can install a licence. A workbench keeps what it
 * holds and works from it read-only; it does not sign out or delete anything on a 402.
 */
import { z } from "zod";
export const DEVICE_CLEAR_SCOPES = ["organisation", "organisation-and-repositories"];
export const deviceClearScopeSchema = z.enum(DEVICE_CLEAR_SCOPES);
/** What the machine is told. */
export const deviceClearSchema = z.object({
    id: z.string().min(1).max(100),
    scope: deviceClearScopeSchema,
    requestedAt: z.string(),
    /** Who asked, in words. */
    by: z.string().max(200).optional(),
    reason: z.string().max(500).optional(),
});
/** `POST /admin/v1/fleet/devices/:id/clear`. `remove`: also revoke the machine's sign-in (it cannot reconnect). */
export const deviceClearRequestSchema = z.object({
    scope: deviceClearScopeSchema.default("organisation"),
    remove: z.boolean().default(true),
    reason: z.string().max(500).optional(),
});
/** `POST /fleet/v1/clear/:id/ack`. */
export const deviceClearAckSchema = z.object({
    state: z.enum(["done", "failed"]),
    /** One or two sentences: what was deleted, or what could not be. */
    message: z.string().max(1000),
    /** For `organisation-and-repositories`: the working copies deleted, by path. */
    repositories: z.array(z.string().max(500)).max(500).optional(),
});
export const DEVICE_CLEAR_STATES = ["pending", "delivered", "done", "failed"];
//# sourceMappingURL=deviceClear.js.map